import { useFileAttachmentsPlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/index.js';
import { useAskComposerPastedTextFile } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/useAskComposerPastedTextFile.js';
import { useComposerAttachmentPreview } from '#pplx/frontend/apps/ask/composer-ui/useComposerAttachmentPreview.js';
import { resolveAskInputAttachmentUploadContext, useAttachmentUploadPolicy, } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useAttachmentUploadPolicy.js';
import { useMediaFileUrlIngress, useMediaImageUrlResolver, } from '#pplx/frontend/apps/ask/hooks/file-attachments-hooks/useMediaFileUrlIngress.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { MAX_CHARACTERS_ALLOWED_TO_SUBMIT } from '#pplx/frontend/components/utils/consts.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const attachmentPreviewSelectedEvent = EVENT_PAYLOAD;
/** Composes Ask attachment policy, recovery and paste notices without owning submission. */
export function useAskComposerAttachments({ backendUUID, attachmentMenuPolicy, characterLimit = MAX_CHARACTERS_ALLOWED_TO_SUBMIT, disabled, isFollowUp, inputLength, querySource, searchModel, selectedSearchMode, searchModeOverride, showFileUpload, hasNonFileAttachments = false, onDismissError, onUploadAttempted, }) {
    const { track } = useInstrumentation();
    const handlePreview = useCallback((presentation) => {
        track(attachmentPreviewSelectedEvent({
            name: 'query attachment preview selected',
            data: { presentation },
        }));
    }, [track]);
    const { onPreviewAttachment, previewOverlay } = useComposerAttachmentPreview({
        onPreview: handlePreview,
    });
    const recoveredPreviewFilesRef = useRef(new Map());
    const handlePreviewAttachment = useCallback((target) => {
        const recoveredFile = target.kind === 'remote' && typeof target.url === 'string'
            ? recoveredPreviewFilesRef.current.get(target.url)
            : undefined;
        onPreviewAttachment(recoveredFile ? { file: recoveredFile, kind: 'local' } : target);
    }, [onPreviewAttachment]);
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    const { reason: uploadReason, resolvedSearchMode, uploadSearchMode, } = useMemo(() => resolveAskInputAttachmentUploadContext({
        querySource,
        searchModeSource: searchModel === undefined
            ? { kind: 'thread-search-mode', threadSearchMode: selectedSearchMode }
            : { kind: 'search-model', getSearchModeForSearchModel, searchModel },
        uploadSearchModeOverride: searchModeOverride,
    }), [getSearchModeForSearchModel, querySource, searchModeOverride, searchModel, selectedSearchMode]);
    const attachmentUploadPolicy = useAttachmentUploadPolicy({
        reason: uploadReason,
        searchMode: uploadSearchMode,
        menuPolicy: attachmentMenuPolicy,
    });
    const { reset: resetPasteNotice, renderAttachmentNotice, showConversion, } = useAskComposerPastedTextFile({ characterLimit, inputLength });
    const attachments = useFileAttachmentsPlugin({
        showFileMetadata: true,
        presentation: hasNonFileAttachments ? 'compact' : 'media',
        uploadsEnabled: showFileUpload && disabled !== true,
        attachmentActionsEnabled: disabled !== true,
        isFollowUp,
        onPreviewAttachment: handlePreviewAttachment,
        onDismissError,
        onUploadAttempted,
        oversizedText: 'file',
        onOversizedTextPasted: showConversion,
        reason: uploadReason,
        uploadPolicy: attachmentUploadPolicy,
        renderAttachmentNotice,
    });
    const { addAttachments: addUploadedAttachments, removeAttachments: removeUploadedAttachments, reset: resetUploadedAttachments, } = attachments;
    useEffect(() => {
        const attachmentUrls = new Set(attachments.attachmentUrls);
        for (const url of recoveredPreviewFilesRef.current.keys()) {
            if (!attachmentUrls.has(url))
                recoveredPreviewFilesRef.current.delete(url);
        }
    }, [attachments.attachmentUrls]);
    const getImageUrl = useMediaImageUrlResolver({ backendUUID, reason: uploadReason });
    const pendingMediaRef = useRef(new Map());
    const [pendingMediaIds, setPendingMediaIds] = useState([]);
    const addRestoredMediaFile = useCallback((file, source) => {
        const recovery = pendingMediaRef.current.get(source.url);
        // A cancelled request must not complete a newer recovery of the same URL.
        if (recovery?.source !== source)
            return;
        // Preview the authorized bytes; submission keeps the storage URL without reuploading.
        recoveredPreviewFilesRef.current.set(source.url, file);
        addUploadedAttachments([recovery.attachment]);
    }, [addUploadedAttachments]);
    const restoreMediaFile = useMediaFileUrlIngress({
        addFile: addRestoredMediaFile,
        getImageUrl,
    });
    const addMediaFile = useCallback((url, filename) => {
        const existing = pendingMediaRef.current.get(url);
        if (existing)
            return existing.promise;
        const source = { filename, url };
        const recovery = {
            source,
            attachment: source,
            promise: restoreMediaFile(source).finally(() => {
                if (pendingMediaRef.current.get(url) !== recovery)
                    return;
                pendingMediaRef.current.delete(url);
                setPendingMediaIds((current) => current.filter((id) => id !== url));
            }),
        };
        pendingMediaRef.current.set(url, recovery);
        setPendingMediaIds((current) => [...current, url]);
        return recovery.promise;
    }, [restoreMediaFile]);
    const addAttachments = useCallback((recoveredAttachments) => {
        recoveredAttachments.forEach((attachment) => {
            const pending = pendingMediaRef.current.get(attachment.url);
            // The caller's recovery gate must start addMediaFile before the queue enriches its metadata.
            if (!pending)
                return;
            pending.attachment = { ...pending.attachment, ...attachment };
        });
    }, []);
    const removeAttachments = useCallback((ids) => {
        ids.forEach((id) => pendingMediaRef.current.delete(id));
        setPendingMediaIds((current) => current.filter((id) => !ids.includes(id)));
        removeUploadedAttachments(ids);
    }, [removeUploadedAttachments]);
    const reset = useCallback(() => {
        pendingMediaRef.current.clear();
        recoveredPreviewFilesRef.current.clear();
        setPendingMediaIds([]);
        resetPasteNotice();
        resetUploadedAttachments();
    }, [resetPasteNotice, resetUploadedAttachments]);
    useEffect(() => {
        const pendingMedia = pendingMediaRef.current;
        const recoveredPreviewFiles = recoveredPreviewFilesRef.current;
        return () => {
            pendingMedia.clear();
            recoveredPreviewFiles.clear();
        };
    }, []);
    useEffect(() => {
        if (!showFileUpload)
            reset();
    }, [reset, showFileUpload]);
    const submitUploads = useMemo(() => [
        ...attachments.submitUploads.filter((upload) => !pendingMediaIds.includes(upload.id)),
        ...pendingMediaIds.map((id) => ({ id, status: 'pending' })),
    ], [attachments.submitUploads, pendingMediaIds]);
    return {
        ...attachments,
        uploadGate: attachmentUploadPolicy.gate,
        previewOverlay,
        addAttachments,
        addMediaFile,
        removeAttachments,
        reset,
        isUploading: attachments.isUploading || pendingMediaIds.length > 0,
        submitUploads,
        resolvedSearchMode,
        reason: uploadReason,
    };
}
//# sourceMappingURL=useAskComposerAttachments.js.map